import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Alert,
  Snackbar,
  Collapse
} from "@mui/material";
import { CheckCircle, ErrorOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { UrlApi } from "../../../../services/apirest";

const FinalizarRendicionDialog = ({ open, onClose, rendicion }) => {
  const [renglones, setRenglones] = useState([]);
  const [details, setDetails] = useState({});
  const [nonMatchingItems, setNonMatchingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [showDiscrepancies, setShowDiscrepancies] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("success");

  const getStockRegistrado = async () => {
    if (!rendicion.uuid) return;
    setIsLoading(true);
    try {
      const resp = await fetch(
        `${UrlApi}/envasado_stock_reparto/xuuid/${rendicion.uuid}`
      );
      const data = await resp.json();
      
      // Establecer valores por defecto en 0 para todos los campos numéricos
      const defaults = data.map((item) => ({
        ...item,
        entradas_llenas: item.entradas_llenas !== null && item.entradas_llenas !== undefined ? item.entradas_llenas : 0,
        vacias_diferencia: item.vacias_diferencia !== null && item.vacias_diferencia !== undefined ? item.vacias_diferencia : 0,
        garrafas_cambio: item.garrafas_cambio !== null && item.garrafas_cambio !== undefined ? item.garrafas_cambio : 0,
      }));
      
      setRenglones(defaults);
      
      // Validar si hay discrepancias iniciales
      setTimeout(() => {
        validateAllRowsOnLoad(defaults);
      }, 100);
    } catch (error) {
      console.error(error);
      setMensaje("Error al cargar los datos");
      setSnackSeverity("error");
      setOpenSnack(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Validar todas las filas al cargar los datos
  const validateAllRowsOnLoad = (rows) => {
    const nonMatching = rows.filter((r) => {
      const total =
        (parseInt(r.entradas_llenas || 0) +
         parseInt(r.vacias_diferencia || 0) +
         parseInt(r.garrafas_cambio || 0));
      return total !== parseInt(r.salidas_llenas || 0);
    });
    
    setNonMatchingItems(nonMatching);
    
    if (nonMatching.length > 0) {
      setShowDiscrepancies(true);
    }
  };

  useEffect(() => {
    if (open) {
      getStockRegistrado();
      setDetails({});
      setNonMatchingItems([]);
      setShowDiscrepancies(false);
    }
  }, [open]);

  const handleChange = (index, field, value) => {
    const updated = [...renglones];
    
    // Si el valor está vacío, establecer en 0
    const numericValue = value === "" ? 0 : Number(value);
    updated[index][field] = numericValue;
    
    setRenglones(updated);
    
    // Validar si hay discrepancia después del cambio
    validateSingleRow(updated[index], index);
  };

  const validateSingleRow = (row, index) => {
    const totalIngresado = 
      (parseInt(row.entradas_llenas || 0) +
       parseInt(row.vacias_diferencia || 0) +
       parseInt(row.garrafas_cambio || 0));
    
    const salidasLlenas = parseInt(row.salidas_llenas || 0);
    
    if (totalIngresado !== salidasLlenas) {
      // Agregar a nonMatchingItems si no está ya
      if (!nonMatchingItems.some(item => item.id === row.id)) {
        setNonMatchingItems(prev => [...prev, row]);
      }
    } else {
      // Remover de nonMatchingItems si coincide
      setNonMatchingItems(prev => prev.filter(item => item.id !== row.id));
      // Limpiar el detalle si ya no hay discrepancia
      if (details[row.id]) {
        const newDetails = {...details};
        delete newDetails[row.id];
        setDetails(newDetails);
      }
    }
  };

  const handleDetailChange = (id, value) => {
    setDetails((prev) => ({ ...prev, [id]: value }));
  };

  const validateAllRows = () => {
    const nonMatching = renglones.filter((r) => {
      const total =
        (parseInt(r.entradas_llenas || 0) +
         parseInt(r.vacias_diferencia || 0) +
         parseInt(r.garrafas_cambio || 0));
      return total !== parseInt(r.salidas_llenas || 0);
    });
    setNonMatchingItems(nonMatching);
    
    if (nonMatching.length > 0) {
      setShowDiscrepancies(true);
      return false;
    }
    
    return true;
  };

  const guardarStock = async () => {
    try {
      const requests = renglones.map((r) =>
        fetch(`${UrlApi}/envasado_stock_reparto/${r.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            entradas_llenas: r.entradas_llenas,
            vacias_diferencia: r.vacias_diferencia,
            garrafas_cambio: r.garrafas_cambio,
            detalle: details[r.id] || null,
          }),
        })
      );
      await Promise.all(requests);
      return true;
    } catch (error) {
      console.error("Error al guardar stock:", error);
      return false;
    }
  };

  const handleSubmit = async () => {
    // Primero validar todas las filas
    const isValid = validateAllRows();
    
    // Si hay discrepancias y no todas tienen explicación, no continuar
    if (!isValid && nonMatchingItems.some(item => !details[item.id] || details[item.id].trim() === "")) {
      setMensaje("Debe completar todas las explicaciones de discrepancia");
      setSnackSeverity("warning");
      setOpenSnack(true);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 1. Actualizar el estado de la rendición
      const rendicionResponse = await fetch(`${UrlApi}/envasado_rendiciones/${rendicion.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          estado: 274,
        }),
      });
      
      if (!rendicionResponse.ok) {
        throw new Error("Error al actualizar el estado de la rendición");
      }
      
      // 2. Guardar el stock
      const stockGuardado = await guardarStock();
      
      if (!stockGuardado) {
        throw new Error("Error al guardar los datos del stock");
      }
      
      // 3. Mostrar mensaje de éxito y cerrar
      setMensaje("Rendición finalizada con éxito");
      setSnackSeverity("success");
      setOpenSnack(true);
      
      setTimeout(() => {
        onClose(true);
      }, 1500);
      
    } catch (error) {
      console.error("Error al finalizar la rendición:", error);
      setMensaje("Error al finalizar la rendición: " + error.message);
      setSnackSeverity("error");
      setOpenSnack(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para verificar si una fila tiene discrepancia
  const hasDiscrepancy = (row) => {
    const totalIngresado = 
      (parseInt(row.entradas_llenas || 0) +
       parseInt(row.vacias_diferencia || 0) +
       parseInt(row.garrafas_cambio || 0));
    
    return totalIngresado !== parseInt(row.salidas_llenas || 0);
  };

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
        <Box justifyContent="center" backgroundColor="#1F618D">
          <DialogTitle>
            <Typography fontSize={20} align="center" color="#ffffff">
              Finalizar Rendición N°{rendicion.nro_rendicion}
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : renglones.length === 0 ? (
            <Typography align="center" color="textSecondary" my={2}>
              No hay productos registrados
            </Typography>
          ) : (
            <>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Complete los valores para cada producto. Los campos se inicializan en 0 para agilizar la carga.
                </Typography>
              </Box>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Producto</TableCell>
                      <TableCell align="center">Salidas Llenas</TableCell>
                      <TableCell align="center">Entradas llenas</TableCell>
                      <TableCell align="center">Entradas Vacías</TableCell>
                      <TableCell align="center">Entradas Cambio</TableCell>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renglones.map((r, idx) => {
                      const totalIngresado = 
                        (parseInt(r.entradas_llenas || 0) +
                         parseInt(r.vacias_diferencia || 0) +
                         parseInt(r.garrafas_cambio || 0));
                      const tieneDiscrepancia = hasDiscrepancy(r);
                      
                      return (
                        <TableRow 
                          key={r.id} 
                          sx={{ 
                            backgroundColor: tieneDiscrepancia ? '#fff9e6' : 'transparent',
                            '&:hover': { backgroundColor: tieneDiscrepancia ? '#fff2cc' : '#fafafa' }
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {r.abreviatura}
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight="bold">
                              {r.salidas_llenas}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              value={r.entradas_llenas}
                              onChange={(e) =>
                                handleChange(idx, "entradas_llenas", e.target.value)
                              }
                              inputProps={{ 
                                min: 0, 
                                pattern: "[0-9]*",
                                style: { textAlign: 'center' }
                              }}
                              type="number"
                              size="small"
                              sx={{ width: 80 }}
                              error={tieneDiscrepancia}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              value={r.vacias_diferencia}
                              onChange={(e) =>
                                handleChange(idx, "vacias_diferencia", e.target.value)
                              }
                              inputProps={{ 
                                min: 0, 
                                pattern: "[0-9]*",
                                style: { textAlign: 'center' }
                              }}
                              type="number"
                              size="small"
                              sx={{ width: 80 }}
                              error={tieneDiscrepancia}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              value={r.garrafas_cambio}
                              onChange={(e) =>
                                handleChange(idx, "garrafas_cambio", e.target.value)
                              }
                              inputProps={{ 
                                min: 0, 
                                pattern: "[0-9]*",
                                style: { textAlign: 'center' }
                              }}
                              type="number"
                              size="small"
                              sx={{ width: 80 }}
                              error={tieneDiscrepancia}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              fontWeight="bold" 
                              color={tieneDiscrepancia ? "#ed6c02" : "text.primary"}
                            >
                              {totalIngresado}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {tieneDiscrepancia ? (
                              <Box display="flex" alignItems="center" justifyContent="center" color="#ed6c02">
                                <ErrorOutline fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="body2">
                                  Ajustar
                                </Typography>
                              </Box>
                            ) : (
                              <Typography color="success.main" variant="body2">
                                OK
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Sección de discrepancias */}
              {nonMatchingItems.length > 0 && (
                <Box mt={3}>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between"
                    p={1.5}
                    sx={{ 
                      backgroundColor: '#f9f9f9', 
                      border: 1, 
                      borderColor: 'grey.300', 
                      borderRadius: 1,
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowDiscrepancies(!showDiscrepancies)}
                  >
                    <Box display="flex" alignItems="center">
                      <ErrorOutline color="warning" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">
                        Se detectaron {nonMatchingItems.length} discrepancia(s)
                      </Typography>
                    </Box>
                    {showDiscrepancies ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  <Collapse in={showDiscrepancies}>
                    <Box mt={1} p={2} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Por favor, proporcione una explicación para cada discrepancia detectada:
                      </Typography>

                      {nonMatchingItems.map((item) => {
                        const totalIngresado =
                          (parseInt(item.entradas_llenas || 0) +
                           parseInt(item.vacias_diferencia || 0) +
                           parseInt(item.garrafas_cambio || 0));

                        return (
                          <Box key={item.id} mb={2}>
                            <Typography variant="body2" gutterBottom>
                              <Box component="span" fontWeight="bold">{item.abreviatura}:</Box> {" "}
                              Salidas registradas: {item.salidas_llenas} | 
                              Total ingresado: {totalIngresado} | 
                              Diferencia: {Math.abs(item.salidas_llenas - totalIngresado)}
                            </Typography>
                            <TextField
                              label="Motivo de la discrepancia"
                              multiline
                              rows={2}
                              fullWidth
                              margin="dense"
                              value={details[item.id] || ""}
                              onChange={(e) => handleDetailChange(item.id, e.target.value)}
                              error={!details[item.id] || details[item.id].trim() === ""}
                              helperText={
                                !details[item.id] || details[item.id].trim() === ""
                                  ? "Este campo es obligatorio para discrepancia"
                                  : ""
                              }
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <Grid container padding={2} direction="row" justifyContent="space-between">
          <Grid item>
            <Button color="inherit" variant="outlined" onClick={() => onClose(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />
              }
              disabled={isSubmitting}
            >
              Finalizar Rendición
            </Button>
          </Grid>
        </Grid>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnack}
        autoHideDuration={3000}
        onClose={() => setOpenSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnack(false)}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {mensaje}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FinalizarRendicionDialog;